const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export interface BrowserCookieAttributes {
  /** Shared parent domain (e.g. `.oddschecker.com`). Omit for a host-only cookie. */
  domain?: string;
  sameSite?: 'Lax' | 'None' | 'Strict';
  secure?: boolean;
}

export class CookieHelper {
  private isBrowser: boolean;

  constructor() {
    this.isBrowser = typeof window !== 'undefined';
  }

  public isString(testString: string): boolean {
    return typeof testString === 'string';
  }

  public getCookie(cookieName: string): string {
    if (!this.isBrowser) return '';

    try {
      const cookieArray = decodeURIComponent(document.cookie)
        .split(';')
        .filter(cookie => {
          const splitCookie = cookie.split('=');

          return splitCookie[0].indexOf(cookieName) !== -1;
        });

      if (cookieArray.length > 0) {
        return cookieArray.shift()?.split('=').pop() || '';
      }

      return '';
    } catch (e) {
      return '';
    }
  }

  public getCookieFromPreferedSource(cookieName: string): string {
    return this.getCookie(cookieName);
  }

  // `attributes` is appended after the existing string rather than folded into it: every caller that
  // passes none must keep producing the exact bytes it always did, or cookies written before this
  // change stop matching the ones written after and the browser quietly keeps both.
  public setBrowserCookie(
    cookieName: string,
    value: string,
    expiry: number,
    attributes: BrowserCookieAttributes = {},
  ): void {
    const { domain, sameSite, secure } = attributes;
    const config = `; expires=${new Date(expiry).toUTCString()} ; path=/;`;
    const extras = `${domain ? ` domain=${domain};` : ''}${sameSite ? ` SameSite=${sameSite};` : ''}${secure ? ' Secure;' : ''}`;
    document.cookie = `${cookieName}=${encodeURIComponent(value)}${config}${extras}`;
  }
}

export interface ICookieService {
  cookieName: string;
  cookieValue: string;
  getArray(): Array<any>;

  getString(): string;
  helper: CookieHelper;
  select(cookieName: string): CookieService;
  set(valueString?: string, expiry?: number, attributes?: BrowserCookieAttributes): void;
  setArrayString(cookieBetIdArray: Array<any>, divider?: string, divider2?: string): string;
}

export class CookieService implements ICookieService {
  cookieValue: string;
  cookieName: string;
  helper: CookieHelper;

  constructor() {
    this.cookieName = '';
    this.cookieValue = '';
    this.helper = new CookieHelper();
  }

  public select(cookieName: string) {
    if (this.helper.isString(cookieName)) {
      this.cookieName = cookieName;
    } else {
      throw 'Cookie name is not a string';
    }

    return this;
  }

  public getString(): string {
    this.cookieValue = this.helper.getCookieFromPreferedSource(this.cookieName);

    if (this.cookieValue !== null) {
      return this.cookieValue;
    }
    return '';
  }

  public getArray(divider = ',', transform?: (T: any) => Array<typeof T>): Array<any> {
    this.cookieValue = this.helper.getCookieFromPreferedSource(this.cookieName);

    if (this.cookieValue !== null) {
      const val = this.cookieValue.split(divider);

      if (transform) {
        return val.map(transform);
      }
      return val;
    }
    return [];
  }

  public setArrayString(cookieBetIdArray: Array<any>, divider = ',', divider2 = '~'): string {
    const cookieString = cookieBetIdArray.map(id => id.join(divider2)).join(divider);

    const expiry = new Date().getTime() + ONE_DAY_MS;

    this.select(this.cookieName).set(cookieString, expiry);

    return cookieString;
  }

  public set(valueString = '', expiry = 0, attributes: BrowserCookieAttributes = {}): void {
    if (typeof window !== 'undefined') {
      this.helper.setBrowserCookie(this.cookieName, valueString, expiry, attributes);
    }
  }
}
